import {createPath} from 'history';
import React, {FC, createContext, useContext, useMemo, useState} from 'react';
import {useLocation} from './useLocation';
import {useQueryParams} from './useQueryParams';
import {RouterContext} from './routerContextHelpers';
import {
  usePublicPort,
  usePublicProtocol,
  // eslint-disable-next-line monorepo/no-internal-import
} from '@aboutyou/config/src/ConfigContext';
// eslint-disable-next-line monorepo/no-internal-import
import {compareAsc} from '@aboutyou/utils/src/compareAsc';

export interface InitialLocation {
  /**
   * @deprecated This protocol is not stable between frontend and server and might cause hydration issues. Please use `usePublicProtocol` from `@aboutyou/config` instead.
   */
  protocol: string;
  host: string;
  pathname: string;
  search?: string;
}

export interface Context {
  initialLocation: InitialLocation;
  routerContext?: RouterContext;
  force404?: boolean;
}

interface InternalContext extends Context {
  resetForced404Page: () => void;
  enableForced404Page: () => void;
}

const ctx = createContext<InternalContext>({
  get initialLocation(): InitialLocation {
    throw new Error(
      'initialLocation is not implemented. Please pick a Router from `@aboutyou/router` and inject it as a parent component.',
    );
  },
  resetForced404Page() {},
  enableForced404Page() {},
  routerContext: undefined,
  force404: false,
});
ctx.displayName = 'ctx';

export const RouterCustomProvider: FC<React.PropsWithChildren<Context>> = ({
  children,
  initialLocation,
  routerContext,
  force404 = false,
}) => {
  const [currentForce404, setForce404] = useState(force404);
  const resetForced404Page = () => setForce404(false);
  const enableForced404Page = () => setForce404(true);

  const value = useMemo(
    () => ({
      initialLocation,
      routerContext,
      force404: currentForce404,
      resetForced404Page,
      enableForced404Page,
    }),
    [initialLocation, routerContext, currentForce404],
  );

  return <ctx.Provider value={value}>{children}</ctx.Provider>;
};

export const useInitialLocation = () => {
  const {initialLocation} = useContext(ctx);
  return initialLocation;
};

export const useRecentFullUrl = () => {
  const {host} = useInitialLocation();
  const protocol = usePublicProtocol();
  const port = usePublicPort();
  const {pathname} = useLocation();
  const search = useMimikVarnishSearch();

  const portSuffix = port ? `:${port}` : '';

  const url = `${protocol}://${host}${
    port && !host.includes(portSuffix) ? portSuffix : ''
  }${createPath({
    pathname,
    search,
  })}`;

  return url;
};

export const useRouterContext = () => {
  const {routerContext} = useContext(ctx);
  return routerContext;
};

export const useForce404Page = () => {
  const {force404} = useContext(ctx);
  return Boolean(force404);
};

export const useResetForced404Page = () => {
  const {resetForced404Page} = useContext(ctx);
  return resetForced404Page;
};

export const useEnableForced404Page = () => {
  const {enableForced404Page} = useContext(ctx);
  return enableForced404Page;
};

/**
 * This sorts the search ascending.
 * The search params needs to be sorted as varnish sorts the query parameters on the server as well to improve the caching. If we don't transform this on the server and the client in the same way, the usage during rendering could cause hydration errors which could potentially break the complete page.
 * The usage of `useMimikVarnishSearch` is just a mitigation as usage of `useLocation.search` or `useHistory().location.search` still return a different result.
 */
const useMimikVarnishSearch = () => {
  const searchParams = useQueryParams({includeUtmParams: false});

  const sortedSearchEntries = Array.from(searchParams.keys())
    .sort(compareAsc)
    .map(key => [key, searchParams.get(key) || '']);
  const sortedSearchParams = new URLSearchParams(sortedSearchEntries);

  return sortedSearchParams.toString();
};
